import type { Report } from 'powerbi-client';

import type { CapturedVisual } from '../../../../lib/app/powerbi/report-visual-capture';
import { captureNamedVisuals } from '../../../../lib/app/powerbi/report-visual-capture';
import type { AskReportData, AskReportDataVisual, AskToolCall } from '../../types/syntiro-agent';
import type { VisualIdsByHandle } from '../../utils/ask-report.util';

export const REPORT_VISUAL_DATA_REQUEST_TOOL_NAME = 'report_visual_data_request';
export const REPORT_VISUAL_DATA_TOOL_LABEL = 'Reading report data...';

const REPORT_DATA_BYTE_BUDGET = 1_500_000;
const POWERBI_EXPORT_ROW_CAP = 30_000;

interface RequestedVisualData {
  visualIds: string[];
  columns?: string[];
}

export interface FulfilVisualsDataRequestOptions {
  toolCall: AskToolCall | undefined;
  report: Report | null;
  visualIdsByHandle: VisualIdsByHandle | null;
}

export function isReportVisualDataRequestToolCall(toolCall: AskToolCall | undefined): boolean {
  return toolCall?.name === REPORT_VISUAL_DATA_REQUEST_TOOL_NAME;
}

function parseRequestedVisualData(
  args: Record<string, unknown> | undefined
): RequestedVisualData | null {
  const visualIds = args?.visualIds;
  if (!Array.isArray(visualIds) || !visualIds.every((id) => typeof id === 'string')) {
    return null;
  }

  const columns = args?.columns;
  if (
    columns !== undefined &&
    (!Array.isArray(columns) || !columns.every((column) => typeof column === 'string'))
  ) {
    return null;
  }

  return { visualIds: visualIds as string[], columns: columns as string[] | undefined };
}

function projectVisual(
  visual: CapturedVisual,
  requestedColumns: string[] | undefined
): { columns: string[]; rows: string[][] } {
  if (!requestedColumns || requestedColumns.length === 0) {
    return { columns: visual.columns, rows: visual.rows };
  }

  const columnIndexes = requestedColumns
    .map((column) => visual.columns.indexOf(column))
    .filter((index) => index !== -1);

  return {
    columns: columnIndexes.map((index) => visual.columns[index]),
    rows: visual.rows.map((row) => columnIndexes.map((index) => row[index]))
  };
}

function estimateRowBytes(row: string[]): number {
  return JSON.stringify(row).length + 1;
}

function toDataVisual(
  visual: CapturedVisual,
  handle: string,
  requestedColumns: string[] | undefined,
  remainingBudget: number
): { dataVisual: AskReportDataVisual; bytesUsed: number } {
  const { columns, rows } = projectVisual(visual, requestedColumns);

  const includedRows: string[][] = [];
  let bytesUsed = 0;
  for (const row of rows) {
    const rowBytes = estimateRowBytes(row);
    if (bytesUsed + rowBytes > remainingBudget) break;
    includedRows.push(row);
    bytesUsed += rowBytes;
  }

  const tooLarge =
    includedRows.length < rows.length || visual.rows.length >= POWERBI_EXPORT_ROW_CAP;

  return {
    dataVisual: {
      handle,
      columns,
      rows: includedRows,
      rowsTotal: visual.rows.length,
      ...(tooLarge ? { tooLarge: true } : {})
    },
    bytesUsed
  };
}

/**
 * Answers a tool call by re-exporting the requested visuals from the live report, so the rows the
 * model reasons over carry the filters, slicer selections and cross-highlighting in force at the
 * moment it asked — not those in force when the manifest was built.
 */
export async function fulfilVisualsDataRequest(
  options: FulfilVisualsDataRequestOptions
): Promise<AskReportData> {
  const { toolCall, report, visualIdsByHandle } = options;
  const requestedVisualData = isReportVisualDataRequestToolCall(toolCall)
    ? parseRequestedVisualData(toolCall?.args)
    : null;

  if (!requestedVisualData) {
    return {
      visuals: [],
      unavailable: [],
      packUnavailable: report === null || visualIdsByHandle === null,
      capturedAtMs: 0
    };
  }

  if (!report || !visualIdsByHandle) {
    return {
      visuals: [],
      unavailable: requestedVisualData.visualIds,
      packUnavailable: true,
      capturedAtMs: 0
    };
  }

  const unavailable: string[] = [];
  const handlesByVisualId = new Map<string, string>();

  requestedVisualData.visualIds.forEach((handle) => {
    const visualId = visualIdsByHandle.get(handle);
    if (visualId === undefined) {
      unavailable.push(handle);
      return;
    }
    handlesByVisualId.set(visualId, handle);
  });

  const capturedAtMs = Date.now();
  const namedVisualCapture = await captureNamedVisuals(report, [...handlesByVisualId.keys()]);

  namedVisualCapture.missingIds.forEach((visualId) => {
    const handle = handlesByVisualId.get(visualId);
    if (handle !== undefined) {
      unavailable.push(handle);
    }
  });

  let remainingBudget = REPORT_DATA_BYTE_BUDGET;
  const visuals = namedVisualCapture.visuals.map((visual) => {
    const { dataVisual, bytesUsed } = toDataVisual(
      visual,
      handlesByVisualId.get(visual.id) ?? visual.id,
      requestedVisualData.columns,
      remainingBudget
    );
    remainingBudget = Math.max(0, remainingBudget - bytesUsed);
    return dataVisual;
  });

  return { visuals, unavailable, packUnavailable: false, capturedAtMs };
}
